/**
 * Screensaver assignment is a pointer on the Slice 1 API.
 * These helpers never build a device command.
 */

export type ScreensaverChoice = "global" | "custom";

export type BrandScreensaverSavePlan =
  | "none"
  | "upload-override"
  | "clear-override";

export function planBrandScreensaverSave(input: {
  mode: ScreensaverChoice;
  hasNewFile: boolean;
  currentSource: "global" | "brand" | null;
}): BrandScreensaverSavePlan {
  if (input.mode === "global") {
    return input.currentSource === "brand" ? "clear-override" : "none";
  }
  if (input.hasNewFile) return "upload-override";
  return "none";
}

export function assertMp4Filename(filename: string): void {
  if (!filename.toLowerCase().endsWith(".mp4")) {
    throw new Error("Screensaver must be an MP4 file");
  }
}

export async function uploadScreensaverLibraryFile(args: {
  file: File;
  sign: (filename: string) => Promise<{ url: string }>;
  put: (url: string, file: File) => Promise<void>;
  create: (filename: string) => Promise<{ releaseId: string }>;
}): Promise<{ releaseId: string }> {
  assertMp4Filename(args.file.name);
  const signed = await args.sign(args.file.name);
  await args.put(signed.url, args.file);
  return args.create(args.file.name);
}

/** Library upload, then the global pointer. No device command. */
export async function replaceGlobalScreensaver(args: {
  file: File;
  sign: (filename: string) => Promise<{ url: string }>;
  put: (url: string, file: File) => Promise<void>;
  create: (filename: string) => Promise<{ releaseId: string }>;
  setGlobal: (releaseId: string) => Promise<void>;
}): Promise<{ releaseId: string }> {
  const uploaded = await uploadScreensaverLibraryFile(args);
  await args.setGlobal(uploaded.releaseId);
  return uploaded;
}

/**
 * Brand profile (and logo) save first. A screensaver failure is reported
 * without undoing those fields and without sending a device command.
 */
function failureMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return fallback;
}

export async function saveBrandThenScreensaver(args: {
  saveBrand: () => Promise<void>;
  applyScreensaver: () => Promise<void>;
}): Promise<{ brandSaved: boolean; screensaverError: string | null }> {
  try {
    await args.saveBrand();
  } catch (err) {
    return { brandSaved: false, screensaverError: failureMessage(err, "Save failed") };
  }
  try {
    await args.applyScreensaver();
    return { brandSaved: true, screensaverError: null };
  } catch (err) {
    return {
      brandSaved: true,
      screensaverError: failureMessage(err, "Screensaver upload failed"),
    };
  }
}
